import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, doc, setDoc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { schoolClassSortOrder, schoolClassSortKey } from '../services/TimetableService';
import { schoolMatches, docInScope, adminSchoolIds } from '../utils/schoolScope';
import { dataCacheService } from '../services/DataCacheService';
import StatCards from './StatCards';
import './AdminPromotionsView.css';
import './AdminDetailPages.css';

const DECISIONS = ['Promoted', 'Retained', 'Graduated', 'Withdrawn'];
const NEEDS_CLASS = (d) => d === 'Promoted' || d === 'Retained';
const ENROLLMENT_STATUS = { Promoted: 'Active', Retained: 'Retained', Graduated: 'Graduated', Withdrawn: 'Withdrawn' };

function AdminPromotionsView({ adminData }) {
  const [section, setSection] = useState('promote');
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [gradeLevels, setGradeLevels] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promotingClass, setPromotingClass] = useState(null);

  const schoolIds = useMemo(() => adminSchoolIds(adminData), [adminData]);

  // Numeric ordering level for a class: gradeLevels.level via gradeId, else
  // the name-based sort key (Kindergarten=0, Grade N=N, Form N=100+N).
  const classLevel = useCallback((cls) => {
    if (cls.gradeId) {
      const gl = gradeLevels.find(g => String(g.id) === String(cls.gradeId));
      if (gl && typeof gl.level === 'number') return gl.level;
    }
    const key = schoolClassSortKey(cls.grade || '', cls.name || '');
    return key[0] === 1000 ? null : key[0];
  }, [gradeLevels]);

  const ck = (p) => `${schoolIds.join('|') || 'all'}:${p}`;

  const loadAll = useCallback(async (force = false) => {
    if (!adminData) return;
    setLoading(true);
    if (force) dataCacheService.invalidate(schoolIds.join('|') || 'all');
    try {
      const [classDocs, yearDocs, gradeDocs, recordDocs] = await Promise.all([
        dataCacheService.getDocs(ck('classes'), collection(db, 'classes')),
        dataCacheService.getDocs(ck('academicYears'), collection(db, 'academicYears')),
        dataCacheService.getDocs(ck('gradeLevels'), collection(db, 'gradeLevels')),
        dataCacheService.getDocs(ck('promotionRecords'), collection(db, 'promotionRecords')),
      ]);

      const classesData = classDocs
        .filter(c => docInScope(c.schoolId, schoolIds))
        .sort(schoolClassSortOrder);
      setClasses(classesData);

      // Global (no schoolId) years + this school's — Super Admin owns these
      setAcademicYears(yearDocs
        .filter(y => !y.schoolId || schoolIds.some(id => schoolMatches(y.schoolId, id)))
        .sort((a, b) => (a.startDate || '').localeCompare(b.startDate || '')));

      setGradeLevels(gradeDocs
        .filter(g => !g.schoolId || schoolIds.some(id => schoolMatches(g.schoolId, id)))
        .sort((a, b) => (a.level ?? 999) - (b.level ?? 999)));

      setRecords(recordDocs
        .filter(r => docInScope(r.schoolId, schoolIds))
        .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')));

      const perClass = await Promise.all(classesData.map(cls =>
        dataCacheService.getDocs(ck(`classes/${cls.id}/students`), collection(db, 'classes', cls.id, 'students'))
      ));
      const allStudents = perClass.flat().filter(s => s.isActive !== false);
      setStudents(allStudents);
      console.log('🔥 AdminPromotions: loaded', classesData.length, 'classes,', allStudents.length, 'students');
    } catch (e) {
      console.error('🔥 AdminPromotions: load error', e);
    } finally {
      setLoading(false);
    }
  }, [adminData, schoolIds]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const studentsInClass = (classId) => students.filter(s => s.classId === classId);

  if (loading) {
    return <div className="loading-container"><div className="spinner"></div><p>Loading...</p></div>;
  }

  if (promotingClass) {
    return (
      <PromoteClassPage
        adminData={adminData}
        fromClass={promotingClass}
        classes={classes}
        students={studentsInClass(promotingClass.id)}
        academicYears={academicYears}
        gradeLevels={gradeLevels}
        classLevel={classLevel}
        onBack={() => setPromotingClass(null)}
        onDone={() => { setPromotingClass(null); loadAll(true); }}
      />
    );
  }

  return (
    <div className="admin-promotions-view">
      <div className="scanner-sub-tabs">
        <button className={`sub-tab ${section === 'promote' ? 'active' : ''}`} onClick={() => setSection('promote')}>⬆️ Promote Classes</button>
        <button className={`sub-tab ${section === 'history' ? 'active' : ''}`} onClick={() => setSection('history')}>📜 Promotion History</button>
      </div>

      <StatCards items={[
        ['Classes', classes.length],
        ['Students', students.length],
        ['Promotion Records', records.length],
        ['This Year', records.filter(r => {
          const active = academicYears.find(y => y.isActive);
          return active && r.toAcademicYearId === active.id;
        }).length],
      ]} />

      {section === 'promote' && (
        <div className="promotion-section">
          <div className="info-banner">
            ℹ️ Tap a class to promote its students into the next academic year. You can move individual students to different classes, retain, graduate, or withdraw them.
          </div>
          {academicYears.length < 2 && (
            <div className="info-banner warning">
              ⚠️ You need at least two academic years (a From and a To year). Ask your Super Admin to create them.
            </div>
          )}
          {classes.length === 0 ? (
            <div className="empty-state"><p>No classes found. Add classes first.</p></div>
          ) : (
            <div className="class-card-grid">
              {classes.map(cls => {
                const count = studentsInClass(cls.id).length;
                return (
                  <div key={cls.id} className="class-card">
                    <div className="class-card-header">
                      <span className="class-icon">🏫</span>
                      <div>
                        <h3>{cls.name}</h3>
                        <p className="class-subtitle">Grade: {cls.grade || gradeLevels.find(g => String(g.id) === String(cls.gradeId))?.name || '—'}</p>
                      </div>
                      <div className="class-count"><strong>{count}</strong><span>students</span></div>
                    </div>
                    <button
                      className="promote-button"
                      disabled={count === 0}
                      onClick={() => setPromotingClass(cls)}
                    >
                      ⬆️ Promote Class
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {section === 'history' && (
        <div className="promotion-section">
          {records.length === 0 ? (
            <div className="empty-state"><p>No promotion records yet — history appears here after you promote students.</p></div>
          ) : (
            records.map(r => (
              <div key={r.id} className="record-card">
                <div className="record-main">
                  <strong>{r.studentName}</strong>
                  <span className="record-id">{r.studentExternalId}</span>
                </div>
                <div className="record-move">
                  {r.fromClassName || '—'} → {r.toClassName || '—'}
                </div>
                <div className="record-meta">
                  <span>{r.fromAcademicYearName} → {r.toAcademicYearName || '—'}</span>
                  <span className={`decision-badge decision-${String(r.decision || '').toLowerCase()}`}>{r.decision}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Per-student promote page ----------

function PromoteClassPage({ adminData, fromClass, classes, students, academicYears, gradeLevels, classLevel, onBack, onDone }) {
  const [fromYearId, setFromYearId] = useState(() => (academicYears.find(y => y.isActive) || academicYears[0] || {}).id || '');
  const [toYearId, setToYearId] = useState('');
  const [defaultToClassId, setDefaultToClassId] = useState('');
  const [rows, setRows] = useState(() => students.map(s => ({ student: s, selected: true, decision: 'Promoted', toClassId: '' })));
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const sourceLevel = classLevel(fromClass);

  // Classes valid as a destination for a decision:
  // Promoted → next grade level; Retained → same level (incl. this class).
  // Unresolvable classes are kept so options never hide; empty → fallback to all.
  const destinationClasses = (decision) => {
    if (!NEEDS_CLASS(decision)) return [];
    const sorted = [...classes].sort(schoolClassSortOrder);
    const allExceptSource = decision === 'Retained' ? sorted : sorted.filter(c => c.id !== fromClass.id);
    if (sourceLevel == null) return allExceptSource;
    const target = decision === 'Promoted' ? sourceLevel + 1 : sourceLevel;
    const filtered = sorted.filter(c => {
      const l = classLevel(c);
      return l == null || l === target;
    }).filter(c => decision === 'Retained' || c.id !== fromClass.id);
    return filtered.length > 0 ? filtered : allExceptSource;
  };

  const selectedRows = rows.filter(r => r.selected);
  const canProcess = selectedRows.length > 0 && fromYearId && toYearId &&
    selectedRows.every(r => !NEEDS_CLASS(r.decision) || r.toClassId || defaultToClassId);

  const setRow = (i, patch) => setRows(prev => prev.map((r, idx) => idx === i ? { ...r, ...patch } : r));

  const nextLevelName = sourceLevel != null
    ? (gradeLevels.find(g => g.level === sourceLevel + 1)?.name || `level ${sourceLevel + 1}`)
    : null;

  const processPromotions = async () => {
    setProcessing(true);
    setError('');
    const fromYear = academicYears.find(y => y.id === fromYearId);
    const toYear = academicYears.find(y => y.id === toYearId);
    const promotedBy = adminData?.username || adminData?.email || 'Admin';

    try {
      for (const row of selectedRows) {
        const s = row.student;
        const toClass = classes.find(c => c.id === (row.toClassId || defaultToClassId)) || null;
        const finalClass = NEEDS_CLASS(row.decision) ? toClass : null;
        const toGrade = finalClass ? gradeLevels.find(g => String(g.id) === String(finalClass.gradeId)) : null;
        const status = ENROLLMENT_STATUS[row.decision];

        // 1. Move/update the roster doc (write new class doc, delete old one)
        const moved = finalClass && finalClass.id !== s.classId;
        const batch = writeBatch(db);
        const updatedStudent = {
          ...s,
          classId: moved ? finalClass.id : s.classId,
          gradeId: (moved && finalClass.gradeId) ? finalClass.gradeId : (s.gradeId || null),
          grade: (moved && finalClass.grade) ? finalClass.grade : (s.grade || ''),
          academicYearId: toYear.id,
          currentEnrollmentStatus: status,
          isActive: row.decision !== 'Withdrawn',
          updatedAt: new Date().toISOString(),
        };
        batch.set(doc(db, 'classes', updatedStudent.classId, 'students', s.id), updatedStudent);
        if (moved) {
          batch.delete(doc(db, 'classes', s.classId, 'students', s.id));
        }
        if (s.studentId) {
          batch.set(doc(db, 'students', s.studentId), {
            studentId: s.studentId,
            schoolId: adminData?.schoolId || null,
            classId: updatedStudent.classId,
            updatedAt: new Date().toISOString(),
          }, { merge: true });
        }
        await batch.commit();

        // 2. Promotion record
        const promoId = crypto.randomUUID();
        const fromGradeName = gradeLevels.find(g => String(g.id) === String(s.gradeId))?.name || s.grade || '';
        await setDoc(doc(db, 'promotionRecords', promoId), {
          id: promoId,
          studentId: s.id,
          studentName: s.fullName || `${s.firstName || ''} ${s.lastName || ''}`.trim(),
          studentExternalId: s.studentId || '',
          fromAcademicYearId: fromYear.id,
          fromAcademicYearName: fromYear.name,
          toAcademicYearId: toYear.id,
          toAcademicYearName: toYear.name,
          fromGradeId: s.gradeId || '',
          fromGradeName,
          toGradeId: toGrade?.id || null,
          toGradeName: toGrade?.name || (finalClass?.grade ?? null),
          fromClassId: s.classId,
          fromClassName: classes.find(c => c.id === s.classId)?.name || '',
          toClassId: finalClass?.id || null,
          toClassName: finalClass?.name || null,
          schoolId: adminData?.schoolId || null,
          decision: row.decision,
          status: 'Completed',
          notes: null,
          promotedBy,
          createdAt: new Date().toISOString(),
        });

        // 3. Enrollment record for the new year
        const enrollId = crypto.randomUUID();
        await setDoc(doc(db, 'enrollmentRecords', enrollId), {
          id: enrollId,
          studentId: s.id,
          studentName: s.fullName || `${s.firstName || ''} ${s.lastName || ''}`.trim(),
          studentExternalId: s.studentId || '',
          academicYearId: toYear.id,
          academicYearName: toYear.name,
          termId: null,
          gradeId: toGrade?.id || s.gradeId || '',
          gradeName: toGrade?.name || finalClass?.grade || fromGradeName,
          classId: updatedStudent.classId,
          className: finalClass?.name || classes.find(c => c.id === updatedStudent.classId)?.name || '',
          schoolId: adminData?.schoolId || null,
          status,
          enrolledAt: new Date().toISOString(),
          promotedAt: row.decision === 'Promoted' ? new Date().toISOString() : null,
          notes: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }
      onDone();
    } catch (e) {
      console.error('🔥 Promotion error:', e);
      setError(e.message || 'Failed to process promotions');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="admin-promotions-view">
      <div className="detail-page">
        <button className="detail-back-button" onClick={onBack}>◄ Back to Promotions</button>

        <div className="detail-card">
          <div className="detail-header">
            <div>
              <h2>Promote {fromClass.name}</h2>
              <p className="detail-subtitle">{students.length} students · {selectedRows.length} selected</p>
            </div>
          </div>

          <div className="year-picker-row">
            <div className="form-group">
              <label>From Academic Year</label>
              <select value={fromYearId} onChange={e => setFromYearId(e.target.value)}>
                <option value="">Select…</option>
                {academicYears.map(y => <option key={y.id} value={y.id}>{y.name}{y.isActive ? ' (Active)' : ''}</option>)}
              </select>
            </div>
            <div className="year-arrow">→</div>
            <div className="form-group">
              <label>To Academic Year</label>
              <select value={toYearId} onChange={e => setToYearId(e.target.value)}>
                <option value="">Select…</option>
                {academicYears.filter(y => y.id !== fromYearId).map(y => <option key={y.id} value={y.id}>{y.name}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Default Target Class</label>
            <span className="field-hint">
              Applies to every selected Promote/Retain row unless overridden below.
              {nextLevelName ? ` Showing ${nextLevelName} classes (the next level after ${fromClass.name}).` : ''}
            </span>
            <select value={defaultToClassId} onChange={e => setDefaultToClassId(e.target.value)}>
              <option value="">Select class…</option>
              {destinationClasses('Promoted').map(c => <option key={c.id} value={c.id}>{c.name}{c.grade ? ` (${c.grade})` : ''}</option>)}
            </select>
          </div>

          <div className="student-rows-header">
            <h4>Students</h4>
            <div>
              <button className="link-button" onClick={() => setRows(prev => prev.map(r => ({ ...r, selected: true })))}>Select All</button>
              <button className="link-button" onClick={() => setRows(prev => prev.map(r => ({ ...r, selected: false })))}>Select None</button>
            </div>
          </div>

          {rows.map((row, i) => {
            const opts = destinationClasses(row.decision);
            const shownClassId = row.toClassId || (NEEDS_CLASS(row.decision) ? defaultToClassId : '');
            const shownClassName = classes.find(c => c.id === shownClassId)?.name;
            return (
              <div key={row.student.id} className={`student-row ${row.selected ? '' : 'row-off'}`}>
                <label className="student-row-main">
                  <input type="checkbox" checked={row.selected} onChange={e => setRow(i, { selected: e.target.checked })} />
                  <span>
                    <strong>{row.student.fullName || `${row.student.firstName || ''} ${row.student.lastName || ''}`}</strong>
                    <span className="record-id"> {row.student.studentId}</span>
                  </span>
                </label>
                {row.selected && (
                  <div className="student-row-controls">
                    <select value={row.decision} onChange={e => setRow(i, { decision: e.target.value, toClassId: '' })}>
                      {DECISIONS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    {NEEDS_CLASS(row.decision) && (
                      <select value={shownClassId} onChange={e => setRow(i, { toClassId: e.target.value })}>
                        <option value="">{shownClassName ? shownClassName : 'Select class…'}</option>
                        {opts.map(c => <option key={c.id} value={c.id}>{c.name}{c.grade ? ` (${c.grade})` : ''}</option>)}
                      </select>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {error && <div className="error-message">{error}</div>}

          <div className="detail-actions">
            <button className="detail-btn-primary" disabled={!canProcess || processing} onClick={processPromotions}>
              {processing ? 'Processing…' : `Process ${selectedRows.length} Student${selectedRows.length === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminPromotionsView;
